import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, ChevronLeft, ChevronRight, TreePine, Snowflake, Mountain, Truck, Pipette, Wrench, Building2, Pickaxe, Phone, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import { EquipmentCard } from '../components/EquipmentCard';
import { useLanguage } from '../context/LanguageContext';
import { ObfuscatedPhone } from '../components/ObfuscatedPhone';
import { ObfuscatedExcavationEmail } from '../components/ObfuscatedExcavationEmail';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Excavation Logo
const EXCAVATION_LOGO = "https://customer-assets.emergentagent.com/job_ef87de67-067e-49a9-b528-fe0fdacc9c16/artifacts/5fgiyhrv_Logo%20001%20-%20Kenscho%20Excavation.png";

// Site Prep & Grading images - CAT 299D3 XE
const SITE_PREP_IMAGES = [
  'https://customer-assets.emergentagent.com/job_build-kenscho/artifacts/r87f977o_maxresdefault.jpg',
  'https://customer-assets.emergentagent.com/job_build-kenscho/artifacts/jn6mnnmu_wtk.jpeg',
  'https://customer-assets.emergentagent.com/job_build-kenscho/artifacts/41xk58z5_CM20200312-2d295-7186d.jpeg',
  'https://customer-assets.emergentagent.com/job_build-kenscho/artifacts/w92bj7zy_CM20190801-38480-903c8.jpeg'
];

// Mini Excavator images - Kubota KX057-4
const MINI_EXCAVATOR_IMAGES = [
  'https://customer-assets.emergentagent.com/job_build-kenscho/artifacts/ck7x7u9l_IMG_6640.jpeg',
  'https://customer-assets.emergentagent.com/job_build-kenscho/artifacts/1tpfyb7h_KX057-Hero-Shot.jpg',
  'https://customer-assets.emergentagent.com/job_build-kenscho/artifacts/xe7zfyhr_IMG_7272.jpeg',
  'https://customer-assets.emergentagent.com/job_build-kenscho/artifacts/dzesz1dk_maxresdefault.jpg'
];

// Where We Work background image (from mini excavator carousel, not the main one)
const WHERE_WE_WORK_IMAGE = 'https://customer-assets.emergentagent.com/job_build-kenscho/artifacts/xe7zfyhr_IMG_7272.jpeg';

export default function Excavation() {
  const [equipment, setEquipment] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeImage, setActiveImage] = useState(0);
  const [activeExcavatorImage, setActiveExcavatorImage] = useState(0);
  const { language, getLocalizedLink } = useLanguage();

  useEffect(() => {
    const fetchEquipment = async () => {
      try {
        const response = await axios.get(`${API}/equipment/excavation`);
        setEquipment(response.data);
      } catch (error) {
        console.error('Error fetching equipment:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchEquipment();
  }, []);

  // Service areas data for "Where We Work" section
  const serviceAreas = language === 'fr' 
    ? [
        { 
          name: 'Lanaudière', 
          desc: 'Région complète',
          cities: ['Rawdon', 'Saint-Calixte', 'Joliette', 'Terrebonne', 'Mascouche', 'Repentigny']
        },
        { 
          name: 'Laurentides', 
          desc: 'Région sud',
          cities: ['St-Jérôme', 'Saint-Sauveur', 'Mirabel', 'Lachute', 'Mont-Tremblant', 'Blainville']
        },
        { 
          name: 'Laval', 
          desc: 'Couverture complète',
          cities: ['Chomedey', 'Vimont', 'Duvernay', 'Sainte-Rose', 'Fabreville', 'Pont-Viau']
        },
        { 
          name: 'Montréal', 
          desc: 'Tous les quartiers',
          cities: ['Plateau', 'Rosemont', 'Villeray', 'Ahuntsic', 'Saint-Laurent', 'Côte-des-Neiges']
        }
      ]
    : [
        { 
          name: 'Lanaudière', 
          desc: 'Full region',
          cities: ['Rawdon', 'Saint-Calixte', 'Joliette', 'Terrebonne', 'Mascouche', 'Repentigny']
        },
        { 
          name: 'Laurentians', 
          desc: 'Southern region',
          cities: ['St-Jérôme', 'Saint-Sauveur', 'Mirabel', 'Lachute', 'Mont-Tremblant', 'Blainville']
        },
        { 
          name: 'Laval', 
          desc: 'Complete coverage',
          cities: ['Chomedey', 'Vimont', 'Duvernay', 'Sainte-Rose', 'Fabreville', 'Pont-Viau']
        },
        { 
          name: 'Montreal', 
          desc: 'All neighborhoods',
          cities: ['Plateau', 'Rosemont', 'Villeray', 'Ahuntsic', 'Saint-Laurent', 'Côte-des-Neiges']
        }
      ];

  const content = {
    en: {
      tagline: 'Kenscho Excavation',
      title1: 'Kenscho',
      title2: 'Excavation',
      desc: 'Kenscho Excavation provides professional earthmoving and site preparation services. From initial land clearing to final grading, Kenscho Excavation handles every phase of your project with precision and expertise.',
      
      // Combined Services (8 total)
      servicesTitle: 'Kenscho Excavation Services',
      servicesSubtitle: 'Professional excavation services for residential, commercial, and industrial projects',
      allServices: [
        { icon: 'tree', label: 'Land Clearing', desc: 'Kenscho Excavation clears brush, trees, and vegetation to prepare your site for construction.' },
        { icon: 'snow', label: 'Snow Clearing', desc: 'Keep your property accessible year-round with professional snow removal from Kenscho Excavation.' },
        { icon: 'mountain', label: 'Grading & Leveling', desc: 'Precision grading by Kenscho Excavation for proper drainage and a solid foundation.' },
        { icon: 'truck', label: 'Material Handling', desc: 'Kenscho Excavation moves materials with forks and offloads trucks in rough terrain.' },
        { icon: 'pipette', label: 'Trenching', desc: 'Kenscho Excavation creates precise trenches for foundations, footings, and drainage systems.' },
        { icon: 'wrench', label: 'Utility Installation', desc: 'French drains, sewer lines, water mains, and underground electrical by Kenscho Excavation.' },
        { icon: 'building', label: 'Demolition', desc: 'Controlled demolition of structures, concrete, and foundations by Kenscho Excavation.' },
        { icon: 'pickaxe', label: 'Emergency Repairs', desc: 'Rapid response from Kenscho Excavation for underground utility breaks and repairs.' }
      ],
      
      // Site Prep Service
      sitePrepTitle: 'Site Prep & Grading',
      sitePrepEquipment: '2019 Cat 299D3 XE',
      sitePrepSubtitle: 'Two-Speed Compact Track Loader',
      sitePrepDesc: 'The Kenscho Excavation Cat 299D3 XE compact track loader is the workhorse of site preparation. With high-flow hydraulics and two-speed output, it delivers exceptional power and precision for any earthmoving task. The rubber tracks minimize ground disturbance while providing superior traction.',
      sitePrepCapabilities: [
        'Tree & stump removal',
        'Rock extraction',
        'Soil screening',
        'Rough terrain access',
        'Precision grading',
        'Debris removal'
      ],
      
      // Mini Excavator Service
      miniExcavatorTitle: 'Trenching & Utilities',
      miniExcavatorEquipment: '2012 Kubota KX057-4',
      miniExcavatorSubtitle: 'Mini Excavator • 47.6 HP • 12,227 lbs',
      miniExcavatorDesc: 'The Kenscho Excavation Kubota KX057-4 mini excavator delivers serious digging power in a compact package. With a maximum digging depth of 12.8 feet and bucket breakout force over 11,000 lbs, it handles demanding excavation tasks while fitting into tight spaces.',
      miniExcavatorCapabilities: [
        '12.8 ft digging depth',
        'Tree & stump extraction',
        'Rock removal',
        'Foundation digging',
        'Tight space access',
        'Thumb attachment'
      ],
      
      // Equipment section
      availableEquipment: 'Available Kenscho Excavation Equipment',
      browseFleet: 'Browse our excavation machinery',
      requestEquipment: 'Request Equipment',
      
      // Where We Work
      serviceAreasTitle: 'Service Areas',
      whereWeWork: 'Where Kenscho Excavation Works',
      areaDesc: 'Kenscho Excavation provides professional excavation services throughout Quebec, including Lanaudière, Laurentians, Laval, and Montreal. Our experienced team and specialized equipment are ready to tackle your project.',
      
      getQuote: 'Get a Quote',
      viewGallery: 'View Gallery',
      excavationCta: 'Ready to get started with Kenscho Excavation?',
      excavationCtaDesc: 'Call now to schedule a consultation or get a quote for your excavation project.',
      excavationCtaButton: 'Get a Quote'
    },
    fr: {
      tagline: 'Excavation Kenscho',
      title1: 'Excavation',
      title2: 'Kenscho',
      desc: 'Excavation Kenscho offre des services professionnels de terrassement et de préparation de terrain. Du défrichage initial au nivellement final, Excavation Kenscho gère chaque phase de votre projet avec précision et expertise.',
      
      // Combined Services (8 total)
      servicesTitle: 'Services Excavation Kenscho',
      servicesSubtitle: 'Services d\'excavation professionnels pour projets résidentiels, commerciaux et industriels',
      allServices: [
        { icon: 'tree', label: 'Défrichage', desc: 'Excavation Kenscho déblaye les broussailles, arbres et végétation pour préparer votre terrain.' },
        { icon: 'snow', label: 'Déneigement', desc: 'Gardez votre propriété accessible toute l\'année avec le déneigement professionnel d\'Excavation Kenscho.' },
        { icon: 'mountain', label: 'Nivellement', desc: 'Nivellement de précision par Excavation Kenscho pour un drainage adéquat et une fondation solide.' },
        { icon: 'truck', label: 'Manutention', desc: 'Excavation Kenscho déplace des matériaux avec des fourches et décharge des camions en terrain accidenté.' },
        { icon: 'pipette', label: 'Tranchées', desc: 'Excavation Kenscho crée des tranchées précises pour fondations, semelles et systèmes de drainage.' },
        { icon: 'wrench', label: 'Installation de services', desc: 'Drains français, égouts, conduites d\'eau et électricité souterraine par Excavation Kenscho.' },
        { icon: 'building', label: 'Démolition', desc: 'Démolition contrôlée de structures, béton et fondations par Excavation Kenscho.' },
        { icon: 'pickaxe', label: 'Réparations d\'urgence', desc: 'Intervention rapide d\'Excavation Kenscho pour bris de services souterrains.' }
      ],
      
      // Site Prep Service
      sitePrepTitle: 'Préparation de terrain',
      sitePrepEquipment: '2019 Cat 299D3 XE',
      sitePrepSubtitle: 'Chargeuse compacte sur chenilles à deux vitesses',
      sitePrepDesc: 'La chargeuse compacte Cat 299D3 XE d\'Excavation Kenscho est le pilier de la préparation de terrain. Avec son système hydraulique à haut débit et sa sortie à deux vitesses, elle offre une puissance et une précision exceptionnelles pour toute tâche de terrassement.',
      sitePrepCapabilities: [
        'Enlèvement d\'arbres et souches',
        'Extraction de roches',
        'Tamisage de sol',
        'Accès terrain difficile',
        'Nivellement de précision',
        'Enlèvement de débris'
      ],
      
      // Mini Excavator Service
      miniExcavatorTitle: 'Tranchées & Services publics',
      miniExcavatorEquipment: '2012 Kubota KX057-4',
      miniExcavatorSubtitle: 'Mini-excavatrice • 47.6 HP • 5545 kg',
      miniExcavatorDesc: 'La mini-excavatrice Kubota KX057-4 d\'Excavation Kenscho offre une puissance de creusage sérieuse dans un format compact. Avec une profondeur de creusage maximale de 3,9 mètres et une force d\'arrachement de plus de 5000 kg, elle gère les tâches exigeantes.',
      miniExcavatorCapabilities: [
        'Profondeur de 3,9 m',
        'Extraction d\'arbres et souches',
        'Enlèvement de roches',
        'Creusage de fondations',
        'Accès espaces restreints',
        'Pince hydraulique'
      ],
      
      // Equipment section
      availableEquipment: 'Équipement Excavation Kenscho disponible',
      browseFleet: 'Parcourez notre machinerie d\'excavation',
      requestEquipment: 'Demander de l\'équipement',
      
      // Where We Work
      serviceAreasTitle: 'Zones de service',
      whereWeWork: 'Où travaille Excavation Kenscho',
      areaDesc: 'Excavation Kenscho offre des services d\'excavation professionnels à travers le Québec, incluant Lanaudière, Laurentides, Laval et Montréal. Notre équipe expérimentée et notre équipement spécialisé sont prêts pour votre projet.',
      
      getQuote: 'Obtenir un devis',
      viewGallery: 'Voir la galerie',
      excavationCta: 'Prêt à commencer avec Excavation Kenscho?',
      excavationCtaDesc: 'Appelez maintenant pour planifier une consultation ou obtenir un devis pour votre projet d\'excavation.',
      excavationCtaButton: 'Obtenir un devis'
    }
  };

  const t = content[language];

  const getServiceIcon = (iconName) => {
    const iconClass = "h-5 w-5";
    switch(iconName) {
      case 'tree': return <TreePine className={iconClass} />;
      case 'snow': return <Snowflake className={iconClass} />;
      case 'mountain': return <Mountain className={iconClass} />;
      case 'truck': return <Truck className={iconClass} />;
      case 'pipette': return <Pipette className={iconClass} />;
      case 'wrench': return <Wrench className={iconClass} />;
      case 'building': return <Building2 className={iconClass} />;
      case 'pickaxe': return <Pickaxe className={iconClass} />;
      default: return <TreePine className={iconClass} />;
    }
  };

  const nextImage = () => setActiveImage((prev) => (prev + 1) % SITE_PREP_IMAGES.length);
  const prevImage = () => setActiveImage((prev) => (prev - 1 + SITE_PREP_IMAGES.length) % SITE_PREP_IMAGES.length);
  
  const nextExcavatorImage = () => setActiveExcavatorImage((prev) => (prev + 1) % MINI_EXCAVATOR_IMAGES.length);
  const prevExcavatorImage = () => setActiveExcavatorImage((prev) => (prev - 1 + MINI_EXCAVATOR_IMAGES.length) % MINI_EXCAVATOR_IMAGES.length);

  // SEO Structured Data
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Excavation Services",
    "provider": {
      "@type": "LocalBusiness",
      "name": language === 'fr' ? "Excavation Kenscho" : "Kenscho Excavation",
      "telephone": "+1-514-291-3737",
      "email": "Excavation@Kenscho.ca",
      "address": { "@type": "PostalAddress", "addressRegion": "QC", "addressCountry": "CA" },
      "areaServed": [
        { "@type": "Place", "name": "Lanaudière" },
        { "@type": "Place", "name": "Laurentians" },
        { "@type": "Place", "name": "Laval" },
        { "@type": "Place", "name": "Montreal" },
        { "@type": "Place", "name": "Rawdon" },
        { "@type": "Place", "name": "St-Jérôme" },
        { "@type": "Place", "name": "Terrebonne" },
        { "@type": "Place", "name": "Mascouche" }
      ]
    },
    "name": language === 'fr' ? "Services Excavation Kenscho" : "Kenscho Excavation Services",
    "description": language === 'fr' 
      ? "Excavation Kenscho - Services d'excavation professionnels: préparation de terrain, nivellement, tranchées, installation de services à Lanaudière, Laurentides, Laval et Montréal."
      : "Kenscho Excavation - Professional excavation services: site prep, grading, trenching, utility installation in Lanaudière, Laurentians, Laval, and Montreal."
  };

  return (
    <div className="grain-texture pt-20" data-testid="excavation-page">
      <Helmet>
        <title>{language === 'fr' 
          ? 'Excavation Kenscho | Terrassement, Nivellement, Tranchées | Lanaudière, Laurentides, Laval, Montréal'
          : 'Kenscho Excavation | Site Prep, Grading, Trenching | Lanaudière, Laurentians, Laval, Montreal'}
        </title>
        <meta name="description" content={language === 'fr'
          ? 'Excavation Kenscho - Services d\'excavation professionnels à Lanaudière, Laurentides, Laval et Montréal. Préparation de terrain, nivellement, tranchées, installation de services. CAT et Kubota. RBQ 5829 4596 01.'
          : 'Kenscho Excavation - Professional excavation services in Lanaudière, Laurentians, Laval, and Montreal. Site prep, grading, trenching, utility installation. CAT and Kubota equipment. RBQ 5829 4596 01.'} />
        <meta name="keywords" content={language === 'fr'
          ? 'Excavation Kenscho, excavation, terrassement, nivellement, tranchées, préparation terrain, mini excavatrice, Lanaudière, Laurentides, Laval, Montréal, CAT, Kubota, Rawdon, St-Jérôme, Terrebonne'
          : 'Kenscho Excavation, excavation, earthmoving, grading, trenching, site prep, mini excavator, Lanaudière, Laurentians, Laval, Montreal, CAT, Kubota, Rawdon, St-Jérôme, Terrebonne'} />
        <link rel="canonical" href={`https://www.kenscho.ca${getLocalizedLink('/excavation')}`} />
        
        <link rel="alternate" hreflang="en" href="https://www.kenscho.ca/excavation" />
        <link rel="alternate" hreflang="fr" href="https://www.kenscho.ca/fr/excavation" />
        <link rel="alternate" hreflang="x-default" href="https://www.kenscho.ca/excavation" />
        
        <meta property="og:title" content={language === 'fr' ? 'Excavation Kenscho | Services d\'excavation professionnels' : 'Kenscho Excavation | Professional Excavation Services'} />
        <meta property="og:description" content={language === 'fr'
          ? 'Excavation Kenscho - Services d\'excavation professionnels. Terrassement, nivellement, tranchées à Lanaudière, Laurentides, Laval et Montréal.'
          : 'Kenscho Excavation - Professional excavation services. Site prep, grading, trenching in Lanaudière, Laurentians, Laval, and Montreal.'} />
        <meta property="og:url" content={`https://www.kenscho.ca${getLocalizedLink('/excavation')}`} />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content={language === 'fr' ? 'fr_CA' : 'en_CA'} />
        
        <meta name="geo.region" content="CA-QC" />
        <meta name="geo.placename" content="Lanaudière, Laurentians, Laval, Montreal" />
        
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      {/* Hero */}
      <section className="py-20 bg-secondary/20">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="mb-8 animate-fade-in-up">
            <img 
              src={EXCAVATION_LOGO} 
              alt={language === 'fr' ? 'Excavation Kenscho' : 'Kenscho Excavation'} 
              className="h-20 md:h-24 w-auto"
            />
          </div>
          <h1 className="text-5xl md:text-6xl font-light tracking-tight mb-6 animate-fade-in-up animation-delay-100">
            {t.title1} <span className="text-secondary">{t.title2}</span>
          </h1>
          <p className="text-lg text-muted-foreground leading-relaxed max-w-2xl animate-fade-in-up animation-delay-200">
            {t.desc}
          </p>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-8">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="bg-secondary/10 border border-secondary/20 rounded-xl p-6 md:p-8">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
              <div>
                <h3 className="text-2xl font-light mb-2">{t.excavationCta}</h3>
                <p className="text-muted-foreground">{t.excavationCtaDesc}</p>
              </div>
              <div className="flex flex-col sm:flex-row items-center gap-6">
                <div className="flex items-center gap-6">
                  <div className="flex items-center gap-2 whitespace-nowrap">
                    <Phone className="h-5 w-5 text-secondary flex-shrink-0" />
                    <ObfuscatedPhone className="font-medium whitespace-nowrap" />
                  </div>
                  <div className="flex items-center gap-2">
                    <Mail className="h-5 w-5 text-secondary flex-shrink-0" />
                    <ObfuscatedExcavationEmail className="font-medium" />
                  </div>
                </div>
                <Button asChild className="rounded-full btn-animate">
                  <Link to={getLocalizedLink('/contact')}>
                    {t.excavationCtaButton} <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Combined Services Section - 8 Service Cards */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-light tracking-tight mb-4">
              {t.servicesTitle}
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {t.servicesSubtitle}
            </p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {t.allServices.map((service, idx) => (
              <div 
                key={idx}
                className="bg-card border border-border/60 rounded-xl p-5 hover:border-secondary/50 transition-colors group"
              >
                <div className="w-10 h-10 bg-secondary/10 rounded-full flex items-center justify-center mb-3 group-hover:bg-secondary/20 transition-colors">
                  <span className="text-secondary">{getServiceIcon(service.icon)}</span>
                </div>
                <h4 className="font-medium mb-1">{service.label}</h4>
                <p className="text-sm text-muted-foreground">{service.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Site Prep & Grading - Bento Style Layout */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {/* Section Header */}
          <div className="mb-12">
            <p className="text-sm font-medium tracking-widest uppercase text-secondary mb-2">
              {t.sitePrepEquipment}
            </p>
            <h2 className="text-4xl md:text-5xl font-light tracking-tight mb-2">
              {t.sitePrepTitle}
            </h2>
            <p className="text-lg text-muted-foreground">
              {t.sitePrepSubtitle}
            </p>
          </div>

          {/* Bento Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Image - Large */}
            <div className="lg:col-span-2 relative group">
              <div className="aspect-[16/10] rounded-2xl overflow-hidden bg-muted">
                <img 
                  src={SITE_PREP_IMAGES[activeImage]} 
                  alt={t.sitePrepTitle}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                {/* Navigation */}
                <button 
                  onClick={prevImage}
                  className="absolute left-4 top-1/2 -translate-y-1/2 p-3 bg-black/50 rounded-full text-white opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/70"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <button 
                  onClick={nextImage}
                  className="absolute right-4 top-1/2 -translate-y-1/2 p-3 bg-black/50 rounded-full text-white opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/70"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
                {/* Counter */}
                <div className="absolute bottom-4 right-4 bg-black/50 text-white text-sm px-3 py-1.5 rounded-full">
                  {activeImage + 1} / {SITE_PREP_IMAGES.length}
                </div>
              </div>
              
              {/* Thumbnail Strip */}
              <div className="flex gap-2 mt-4">
                {SITE_PREP_IMAGES.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setActiveImage(idx)}
                    className={`flex-1 aspect-video rounded-lg overflow-hidden transition-all ${
                      idx === activeImage 
                        ? 'ring-2 ring-secondary ring-offset-2' 
                        : 'opacity-60 hover:opacity-100'
                    }`}
                  >
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            </div>

            {/* Right Column - Description & Capabilities */}
            <div className="flex flex-col gap-6">
              {/* Description Card */}
              <div className="bg-card border border-border/60 rounded-2xl p-6 flex-1">
                <p className="text-muted-foreground leading-relaxed">
                  {t.sitePrepDesc}
                </p>
              </div>
              
              {/* Capabilities */}
              <div className="bg-secondary/10 rounded-2xl p-6">
                <h4 className="font-medium mb-4 text-secondary">{language === 'fr' ? 'Capacités' : 'Capabilities'}</h4>
                <div className="flex flex-wrap gap-2">
                  {t.sitePrepCapabilities.map((cap, idx) => (
                    <span 
                      key={idx}
                      className="px-3 py-1.5 bg-background border border-border/60 rounded-full text-sm"
                    >
                      {cap}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="mt-12 text-center">
            <Button asChild size="lg" className="rounded-full">
              <Link to={getLocalizedLink('/contact')}>
                {t.getQuote} <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Mini Excavator - Kubota KX057-4 - Mirrored Layout */}
      <section className="py-24 bg-muted/30">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {/* Section Header */}
          <div className="mb-12">
            <p className="text-sm font-medium tracking-widest uppercase text-secondary mb-2">
              {t.miniExcavatorEquipment}
            </p>
            <h2 className="text-4xl md:text-5xl font-light tracking-tight mb-2">
              {t.miniExcavatorTitle}
            </h2>
            <p className="text-lg text-muted-foreground">
              {t.miniExcavatorSubtitle}
            </p>
          </div>

          {/* Bento Grid - Mirrored (Image on Right) */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column - Description & Capabilities */}
            <div className="flex flex-col gap-6 order-2 lg:order-1">
              {/* Description Card */}
              <div className="bg-card border border-border/60 rounded-2xl p-6 flex-1">
                <p className="text-muted-foreground leading-relaxed">
                  {t.miniExcavatorDesc}
                </p>
              </div>
              
              {/* Capabilities */}
              <div className="bg-secondary/10 rounded-2xl p-6">
                <h4 className="font-medium mb-4 text-secondary">{language === 'fr' ? 'Capacités' : 'Capabilities'}</h4>
                <div className="flex flex-wrap gap-2">
                  {t.miniExcavatorCapabilities.map((cap, idx) => (
                    <span 
                      key={idx}
                      className="px-3 py-1.5 bg-background border border-border/60 rounded-full text-sm"
                    >
                      {cap}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Main Image - Large */}
            <div className="lg:col-span-2 relative group order-1 lg:order-2">
              <div className="aspect-[16/10] rounded-2xl overflow-hidden bg-muted">
                <img 
                  src={MINI_EXCAVATOR_IMAGES[activeExcavatorImage]} 
                  alt={t.miniExcavatorTitle}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                {/* Navigation */}
                <button 
                  onClick={prevExcavatorImage}
                  className="absolute left-4 top-1/2 -translate-y-1/2 p-3 bg-black/50 rounded-full text-white opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/70"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <button 
                  onClick={nextExcavatorImage}
                  className="absolute right-4 top-1/2 -translate-y-1/2 p-3 bg-black/50 rounded-full text-white opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/70"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
                {/* Counter */}
                <div className="absolute bottom-4 right-4 bg-black/50 text-white text-sm px-3 py-1.5 rounded-full">
                  {activeExcavatorImage + 1} / {MINI_EXCAVATOR_IMAGES.length}
                </div>
              </div>
              
              {/* Thumbnail Strip */}
              <div className="flex gap-2 mt-4">
                {MINI_EXCAVATOR_IMAGES.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setActiveExcavatorImage(idx)}
                    className={`flex-1 aspect-video rounded-lg overflow-hidden transition-all ${
                      idx === activeExcavatorImage 
                        ? 'ring-2 ring-secondary ring-offset-2' 
                        : 'opacity-60 hover:opacity-100'
                    }`}
                  >
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="mt-12 text-center">
            <Button asChild size="lg" className="rounded-full">
              <Link to={getLocalizedLink('/contact')}>
                {t.getQuote} <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Equipment Grid - Only show if equipment exists */}
      {equipment.length > 0 && (
        <section className="py-24">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
              <div>
                <h2 className="text-3xl font-light tracking-tight">
                  {t.availableEquipment}
                </h2>
                <p className="text-muted-foreground mt-2">
                  {t.browseFleet}
                </p>
              </div>
              <Button asChild variant="outline" className="rounded-full">
                <Link to={getLocalizedLink('/contact')}>
                  {t.requestEquipment} <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>

            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="bg-muted animate-pulse h-96 rounded-lg" />
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {equipment.map((item) => (
                  <div key={item.id} className="stagger-item">
                    <EquipmentCard equipment={item} />
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Where We Work - Service Areas Section */}
      <section className="py-24 bg-muted/30">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-sm font-medium tracking-widest uppercase text-secondary mb-4">
                {t.serviceAreasTitle}
              </p>
              <h2 className="text-4xl md:text-5xl font-light tracking-tight mb-8">
                {t.whereWeWork}
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed mb-8">
                {t.areaDesc}
              </p>
              
              <div className="grid grid-cols-2 gap-6">
                {serviceAreas.map((area) => (
                  <div key={area.name} className="p-6 bg-card border border-border/60 rounded-lg">
                    <h3 className="text-xl font-medium mb-1">{area.name}</h3>
                    <p className="text-muted-foreground text-sm mb-3">{area.desc}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {area.cities.map((city) => (
                        <span key={city} className="text-xs px-2 py-0.5 bg-muted rounded-full text-muted-foreground">
                          {city}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="aspect-square rounded-lg overflow-hidden bg-muted">
              <img 
                src={WHERE_WE_WORK_IMAGE}
                alt={language === 'fr' ? 'Excavation Kenscho - Mini excavatrice Kubota' : 'Kenscho Excavation - Kubota mini excavator'}
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <h2 className="text-3xl md:text-4xl font-light tracking-tight mb-6">
            {language === 'fr' ? 'Un projet d\'excavation en tête?' : 'Have an Excavation Project in Mind?'}
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-10">
            {language === 'fr' 
              ? 'Contactez Excavation Kenscho aujourd\'hui pour une consultation gratuite et un devis personnalisé pour votre projet.'
              : 'Contact Kenscho Excavation today for a free consultation and personalized quote for your project.'}
          </p>
          <Button asChild size="lg" className="rounded-full px-8 btn-animate" data-testid="excavation-cta-btn">
            <Link to={getLocalizedLink('/contact')}>
              {language === 'fr' ? 'Obtenir un devis gratuit' : 'Get a Free Quote'} <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </Button>
        </div>
      </section>
    </div>
  );
}
